import { createBrowserRouter, Link, RouterProvider } from 'react-router-dom';
import { AuthProvider } from '@/auth/AuthProvider';
import { RequireAdmin } from '@/auth/RequireAdmin';
import { AppShell } from '@/components/layout/AppShell';
import { FullScreenMessage } from '@/components/ui/FullScreenMessage';
import { webConfig } from '@/config/env';
import { DemoBanner } from '@/demo/DemoBanner';
import { IS_DEMO } from '@/demo/demoMode';
import { HomePage } from '@/pages/HomePage';
import { JobsPage } from '@/pages/JobsPage';
import { LoginPage } from '@/pages/LoginPage';
import { MorePage } from '@/pages/MorePage';
import { ArchivePage } from '@/pages/ArchivePage';
import { EditJobPage, JobDetailsPage, NewJobPage } from '@/pages/JobPages';
import { PersonalPage, RegisterPage } from '@/pages/ContractorPages';
import { ContractorDetailPage, ContractorsPage } from '@/pages/ContractorsPages';
import { MessagesPage, ThreadPage } from './pages/MessagesPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { RequestsPage } from '@/pages/RequestsPage';
import { ToastProvider } from '@/components/ui/Toast';
import { SettingsPage } from '@/pages/SettingsPage';
import { DevPage } from '@/pages/DevPage';
import { RouteError } from '@/components/RouteError';

function NotFound() {
  return (
    <FullScreenMessage title="הדף לא נמצא" body="ייתכן שהקישור שגוי או שהדף הוסר.">
      <Link to="/" className="font-semibold text-brand hover:underline">
        למסך הבית
      </Link>
    </FullScreenMessage>
  );
}

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage />, errorElement: <RouteError /> },

  // מסכי קבלן ועובד — ציבוריים, בלי התחברות
  { path: '/j/:slug', element: <RegisterPage />, errorElement: <RouteError /> },
  { path: '/c/:token', element: <PersonalPage />, errorElement: <RouteError /> },

  // אזור ניהול — רק אחרי אישור השרת
  {
    element: <RequireAdmin />,
    errorElement: <RouteError />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'jobs', element: <JobsPage /> },
          { path: 'jobs/new', element: <NewJobPage /> },
          { path: 'jobs/:jobId', element: <JobDetailsPage /> },
          { path: 'jobs/:jobId/edit', element: <EditJobPage /> },
          { path: 'requests', element: <RequestsPage /> },
          { path: 'contractors', element: <ContractorsPage /> },
          { path: 'contractors/:contractorId', element: <ContractorDetailPage /> },
          { path: 'notifications', element: <NotificationsPage /> },
          { path: 'messages', element: <MessagesPage /> },
          { path: 'messages/:threadId', element: <ThreadPage /> },
          { path: 'archive', element: <ArchivePage /> },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'more', element: <MorePage /> },
          { path: 'dev', element: <DevPage /> },
        ],
      },
    ],
  },
  { path: '*', element: <NotFound /> },
]);

export function App() {
  if (!webConfig.ok && !IS_DEMO) {
    return (
      <FullScreenMessage
        title="האפליקציה עוד לא הוגדרה"
        body="חסרים ערכי Firebase בקובץ .env.local. יש להעתיק את .env.example ולמלא את הערכים (ראו README)."
      >
        <ul className="ltr rounded-control bg-sunken px-4 py-3 text-start text-[13px] text-ink-2">
          {webConfig.missing.map((k) => (
            <li key={k}>{k}</li>
          ))}
        </ul>
      </FullScreenMessage>
    );
  }

  return (
    <AuthProvider>
      <ToastProvider>
        <DemoBanner />
        <RouterProvider router={router} />
      </ToastProvider>
    </AuthProvider>
  );
}
